import { type AnalyticsDispatcher } from 'shisell';
import { withExtras, withMeta } from 'shisell/extenders';
import { BehaviorSubject } from 'rxjs';
import {
  ExtendAnalyticsPayload,
  HostToIframeMessage,
  IframeToHostMessage,
  WidgetState,
  type WidgetActionHandler,
  type WidgetActions,
  WidgetActionMethod,
} from './types';
import { Configs as BaseConfigs, Options as BaseOptions } from '@asurion-core/widget-client/lib/types/WidgetVariant';
import { getUserBrowser, type UserBrowser } from '@automation-suite/shared-clients-user-browser';
import { logger } from '@automation-suite/shared-clients-widget-logger';
import { parseSearchParams } from '@automation-suite/shared-utils-helpers';
import { AnalyticsEventName } from '@automation-suite/shared-clients-analytics-dispatcher';
import {
  createMessageConsumer,
  createMessageProducer,
  MessageConsumer,
  MessageProducer,
} from '@automation-suite/shared-clients-window-connection';
import { ViewportType } from './types/ViewportChangePayload';
import { observeUrlChanges, type UrlChangeDetail, type CleanupFunction } from './utils/observeUrlChanges';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from '@automation-suite/shared-utils-body-scroll-lock';

export type WidgetParams<
  Configs extends Record<string, unknown> | undefined,
  PartialOptions extends Record<string, unknown> | undefined,
> = {
  analyticsDispatcher: AnalyticsDispatcher<Promise<void>>;
  configs: Configs;
  optionOverrides: PartialOptions;
  namespace: string;
  loaderUrl?: string;
};

export type WidgetParamsWithoutConfigs<PartialOptions extends Record<string, unknown> | undefined> = Omit<
  WidgetParams<undefined, PartialOptions>,
  'configs'
>;

export type WidgetParamsWithoutOptions<Configs extends Record<string, unknown> | undefined> = Omit<
  WidgetParams<Configs, undefined>,
  'optionOverrides'
>;

export type WidgetParamsWithoutOptionsOrConfigs = Omit<WidgetParams<undefined, undefined>, 'configs' | 'options'>;

export abstract class Widget<
  Actions extends WidgetActions,
  Configs extends BaseConfigs,
  PartialOptions extends BaseOptions,
  IframeToHostMessageConsumer extends MessageConsumer<
    IframeToHostMessage<
      PartialOptions extends undefined ? undefined : WidgetParams<Configs, PartialOptions>['optionOverrides']
    >
  >,
  HostToIframeMessageProducer extends MessageProducer<
    HostToIframeMessage<
      WidgetParams<Configs, PartialOptions>['configs'],
      WidgetParams<Configs, PartialOptions>['optionOverrides']
    >
  > = MessageProducer<
    HostToIframeMessage<
      WidgetParams<Configs, PartialOptions>['configs'],
      WidgetParams<Configs, PartialOptions>['optionOverrides']
    >
  >,
> {
  protected analyticsDispatcher: WidgetParams<Configs, PartialOptions>['analyticsDispatcher'];
  protected readonly configs: Configs extends undefined ? undefined : WidgetParams<Configs, PartialOptions>['configs'];
  protected readonly namespace: WidgetParams<Configs, PartialOptions>['namespace'];
  protected readonly optionOverrides: PartialOptions extends undefined
    ? undefined
    : WidgetParams<Configs, PartialOptions>['optionOverrides'];
  protected readonly iframeUrl: URL;
  protected readonly loaderUrl: URL;
  protected _options:
    | (PartialOptions extends undefined ? undefined : WidgetParams<Configs, PartialOptions>['optionOverrides'])
    | undefined;
  protected readonly optionsSubject$ = new BehaviorSubject<PartialOptions | undefined>(undefined);
  protected _state: WidgetState = 'INITIALIZING';
  protected readonly stateSubject$ = new BehaviorSubject<WidgetState>('INITIALIZING');
  protected readonly iframeId: string;
  protected parentElement: HTMLElement | undefined;
  protected iframeElement: HTMLElement | undefined;
  protected isDesktopMediaQuery = window.matchMedia(`(min-width: 768px)`);
  private isDesktopMediaQueryChangeEventHandler: (() => void) | undefined;
  private urlChangeCleanup: CleanupFunction | undefined;
  protected iframeToHostMessageConsumer: IframeToHostMessageConsumer | undefined;
  protected hostToIframeMessageProducer: HostToIframeMessageProducer | undefined;
  protected readonly userBrowser: UserBrowser | undefined;

  constructor(
    props: Configs extends undefined
      ? PartialOptions extends undefined
        ? WidgetParamsWithoutOptionsOrConfigs
        : WidgetParamsWithoutConfigs<PartialOptions>
      : PartialOptions extends undefined
        ? WidgetParamsWithoutOptions<Configs>
        : WidgetParams<Configs, PartialOptions>
  ) {
    try {
      this.userBrowser = getUserBrowser();
    } catch {
      logger.error('Failed to initialize userBrowser; widget will continue without browser identity');
    }
    this.namespace = props.namespace;
    this.iframeId = `${this.namespace}_iframe`;

    // NOTE: Initialize loaderUrl and iframeUrl in the constructor (not as property initializers) because:
    // 1. getIframeUrl() depends on this.loaderUrl being set first
    // 2. document.currentScript is only available during synchronous script execution.
    //    When widgets are created in async contexts (e.g., IntersectionObserver callbacks),
    //    document.currentScript is null, so we allow passing loaderUrl via props.
    this.loaderUrl = new URL(props.loaderUrl ?? (document.currentScript as HTMLScriptElement)?.src);
    this.iframeUrl = this.getIframeUrl();

    if ('configs' in props) {
      this.configs = props.configs as Configs extends undefined ? undefined : Configs;
    } else {
      this.configs = undefined as Configs extends undefined ? undefined : Configs;
    }

    if ('optionOverrides' in props) {
      this.optionOverrides = props.optionOverrides as PartialOptions extends undefined ? undefined : PartialOptions;
    } else {
      this.optionOverrides = undefined as PartialOptions extends undefined ? undefined : PartialOptions;
    }

    const hostUrl = window.location.href;
    const hostOrigin = window.location.origin;
    const hostPath = window.location.pathname;
    const hostQueryParams = parseSearchParams(window.location.search);
    const widgetUrl = this.iframeUrl.href;
    const widgetOrigin = this.iframeUrl.origin;
    const widgetPath = this.iframeUrl.pathname;
    const widgetQueryParams = parseSearchParams(this.iframeUrl.search);
    const widgetId = this.configs?.simplr?.widgetId;
    const parentElementId = this.configs?.parentElementId;

    logger.setContext((currentContext) => ({
      ...currentContext,
      browserId: this.userBrowser?.Id ?? '',
      sessionId: this.userBrowser?.SessionId ?? '',
      widgetId,
      widgetUrl,
      widgetOrigin,
      widgetPath,
      widgetQueryParams,
      parentElementId,
      hostUrl,
      hostOrigin,
      hostPath,
      hostQueryParams,
    }));

    this.analyticsDispatcher = props.analyticsDispatcher?.extend(
      withExtras({
        WidgetId: widgetId,
        WidgetUrl: widgetUrl,
        WidgetOrigin: widgetOrigin,
        WidgetPath: widgetPath,
        WidgetQueryParams: widgetQueryParams,
        ParentElementId: parentElementId,
      })
    );
  }

  public get options() {
    return this._options;
  }

  public set options(newOptions) {
    this._options = newOptions;
    this.optionsSubject$.next(newOptions);
  }

  public get state() {
    return this.stateSubject$.value;
  }

  public set state(newState) {
    this._state = newState;
    this.stateSubject$.next(newState);
  }

  private async findContainer(id: string, retries = 10, delay = 100): Promise<HTMLElement> {
    const container = document.getElementById(id);
    if (container) return container;
    if (retries === 0) {
      const error = new Error(`Widget: Container element with id "${id}" not found after 1 second.`);
      logger.error(error);
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
    return this.findContainer(id, retries - 1, delay);
  }

  public async init() {
    await this.destroy();
    this.state = 'INITIALIZING';
    this.parentElement = this.configs?.parentElementId
      ? await this.findContainer(this.configs.parentElementId)
      : window.document.body;

    const iframeElement = document.createElement('iframe');
    iframeElement.src = this.iframeUrl.href;
    iframeElement.id = this.iframeId;
    iframeElement.style.display = 'none';
    iframeElement.allow = 'clipboard-read; clipboard-write; camera; microphone;';
    iframeElement.setAttribute('aria-label', 'Asurion Widget');
    iframeElement.setAttribute('scrolling', 'no');
    Object.assign(iframeElement.style, this.getIframeStyles(this.isDesktopMediaQuery.matches));

    this.isDesktopMediaQueryChangeEventHandler = this.onDesktopMediaQueryChange.bind(this);
    this.isDesktopMediaQuery.addEventListener('change', this.isDesktopMediaQueryChangeEventHandler);

    this.urlChangeCleanup = observeUrlChanges(this.handleUrlChange.bind(this));

    this.iframeToHostMessageConsumer = createMessageConsumer({
      allowedTargetOrigins: [this.iframeUrl.origin],
    }) as IframeToHostMessageConsumer;

    this.iframeToHostMessageConsumer.addListener('analytic', (event) => {
      this.analyticsDispatcher?.dispatch(event?.Name, event);
    });

    this.iframeToHostMessageConsumer.addListener('extend-analytics', this.extendAnalytics.bind(this));
    await this.beforeMount();

    const iframeReadyPromise = this.iframeToHostMessageConsumer.addAsyncListener('iframe-ready');
    this.iframeElement = iframeElement;
    this.parentElement.appendChild(iframeElement);
    await iframeReadyPromise;

    this.hostToIframeMessageProducer = createMessageProducer({
      targetOrigin: this.iframeUrl.origin,
      targetWindow: iframeElement.contentWindow,
    }) as HostToIframeMessageProducer;

    const widgetInitializedPromise = this.iframeToHostMessageConsumer?.addAsyncListener('widget-initialized');

    this.hostToIframeMessageProducer?.sendMessage({
      payload: {
        type: this.isDesktopMediaQuery.matches ? ViewportType.Desktop : ViewportType.Mobile,
      },
      type: 'viewport-change',
    });

    this.hostToIframeMessageProducer?.sendMessage({
      payload: {
        configs: this.configs,
        optionOverrides: this.optionOverrides,
        userBrowser: { browserId: this.userBrowser?.Id ?? '', sessionId: this.userBrowser?.SessionId ?? '' },
      },
      type: 'initialize-widget',
    });

    const widgetInitializedPayload = await widgetInitializedPromise;

    iframeElement.style.display = 'block';

    this.options = widgetInitializedPayload?.options;
    if (this.options?.analytics?.partnerId) {
      this.extendAnalytics({
        MetaData: {
          Client: this.options.analytics.partnerId,
        },
        ExtraData: {
          Partner: this.options.analytics.partnerId,
        },
      });
    }

    await this.afterMount();

    this.state = 'ACTIVE';
    this.analyticsDispatcher?.dispatch(AnalyticsEventName.Business, {
      ExtraData: {
        id: 'Initialized',
        'data-analytics-id': 'Initialized',
      },
    });
  }

  protected async beforeMount(): Promise<void> {
    // default: noop
  }

  protected async afterMount(): Promise<void> {
    // default: noop
  }

  abstract getIframeStyles(isDesktop: boolean): Partial<CSSStyleDeclaration>;

  protected abstract getIframeUrl(): URL;

  public async destroy() {
    this.iframeToHostMessageConsumer?.destroy();

    if (this.isDesktopMediaQueryChangeEventHandler) {
      this.isDesktopMediaQuery.removeEventListener('change', this.isDesktopMediaQueryChangeEventHandler);
      this.isDesktopMediaQueryChangeEventHandler = undefined;
    }

    if (this.urlChangeCleanup) {
      this.urlChangeCleanup();
      this.urlChangeCleanup = undefined;
    }

    if (this.iframeElement && this.parentElement) {
      this.parentElement.removeChild(this.iframeElement);
    }

    clearAllBodyScrollLocks();

    this.hostToIframeMessageProducer = undefined;
    this.iframeToHostMessageConsumer = undefined;
    this.iframeElement = undefined;
    this.parentElement = undefined;
    this.options = undefined;
    this.state = 'DESTROYED';
  }

  private onDesktopMediaQueryChange() {
    if (this.iframeElement) {
      const isDesktop = this.isDesktopMediaQuery.matches;
      Object.assign(this.iframeElement.style, this.getIframeStyles(isDesktop));
      this.hostToIframeMessageProducer?.sendMessage({
        payload: {
          type: isDesktop ? ViewportType.Desktop : ViewportType.Mobile,
        },
        type: 'viewport-change',
      });
    }
  }

  async execute<N extends keyof Actions>(method: N, ...params: unknown[]) {
    if (!Object.getOwnPropertyDescriptor(this.actions, method)) {
      throw new Error(`Unknown action`);
    } else {
      this.analyticsDispatcher?.dispatch('Widget_SDK_ACTION', {
        ExtraData: { 'data-analytics-id': method, id: method },
      });
      await (this.actions[method] as WidgetActionHandler)(...params);
    }
  }

  protected extendAnalytics(payload: ExtendAnalyticsPayload) {
    if (this.analyticsDispatcher) {
      if (payload.ExtraData) {
        this.analyticsDispatcher = this.analyticsDispatcher.extend(withExtras(payload.ExtraData));
      }

      if (payload.MetaData) {
        const metaExtenders = Object.entries(payload.MetaData).map(([key, value]) => withMeta(key, value));

        if (metaExtenders.length > 0) {
          this.analyticsDispatcher = this.analyticsDispatcher.extend(...metaExtenders);
        }
      }
    }
  }

  protected _actions: WidgetActions = {
    [WidgetActionMethod.Init]: async () => {
      await this.init();
    },
    [WidgetActionMethod.Destroy]: async () => {
      await this.destroy();
    },
    [WidgetActionMethod.ExtendContext]: (context) => {
      this.hostToIframeMessageProducer?.sendMessage({
        payload: context,
        type: 'extend-context',
      });
    },
    [WidgetActionMethod.Subscribe]: (subject, callback) => {
      if (subject === 'options') {
        return this.optionsSubject$.subscribe(callback);
      } else if (subject === 'state') {
        return this.stateSubject$.subscribe(callback);
      }
    },
    [WidgetActionMethod.ExtendTrackingParameters]: (trackingParameters) => {
      this.hostToIframeMessageProducer?.sendMessage({
        payload: trackingParameters,
        type: 'extend-tracking-parameters',
      });
    },
    [WidgetActionMethod.SetLanguage]: (language) => {
      this.hostToIframeMessageProducer?.sendMessage({
        payload: language,
        type: 'set-language',
      });
    },
    [WidgetActionMethod.RegisterForPushNotifications]: (params) => {
      this.hostToIframeMessageProducer?.sendMessage({
        payload: params,
        type: 'register-for-push-notifications',
      });
    },
  };

  private handleUrlChange(detail: UrlChangeDetail): void {
    const url = new URL(detail.newURL);
    this.hostToIframeMessageProducer?.sendMessage({
      type: 'extend-more-ticket-info',
      payload: {
        userOriginUrl: url.href,
        hostPageUrl: url.href,
        hostPageOrigin: url.origin,
        hostPagePath: url.pathname,
        hostPageQueryParams: parseSearchParams(url.search),
      },
    });
  }

  protected buildUrl(origin: string, ...paths: string[]) {
    const useLoaderBasePath = origin === this.loaderUrl.origin;
    const loaderBasePath = useLoaderBasePath ? this.loaderUrl.pathname.split('/').slice(0, -1).filter(Boolean) : [];

    return new URL(
      [
        origin,
        ...loaderBasePath,
        ...paths,
        process.env.GIT_REVISION_HASH ? process.env.GIT_REVISION_HASH : undefined,
        'index.html',
      ]
        .filter(Boolean)
        .join('/')
    );
  }

  protected preventParentScroll() {
    if (this.iframeElement) {
      disableBodyScroll(this.iframeElement, {
        reserveScrollBarGap: true,
      });
    }
  }

  protected allowParentScroll() {
    if (this.iframeElement) {
      enableBodyScroll(this.iframeElement);
    }
  }

  protected abstract actions: Actions;
}
